import type { Node, NodeId } from "~/models/dto";
import { type RequestStatus, type ResponseStatus, errorStatus, successStatus } from "~/tools/request-status";

import { request, toRequestObservable } from "../generic/request";

export const requestNodeUnlock = <T extends Node>(node: T): Promise<ResponseStatus<Error, T>> =>
	request<T>(`/api/node/${node._type.toLocaleLowerCase()}/${node.id!}/unlock`, {
		method: "POST",
	})
		.then(successStatus)
		.catch(errorStatus);

export interface RequestNodeProps {
	type?: string;
	id: NodeId;
}

export const requestNode = <T extends Node>(
	props: RequestNodeProps,
): Promise<RequestStatus<Error, T, RequestNodeProps>> =>
	request<T>(`/api/node/_/${props.id}`, {
		query: {
			timeoutSeconds: 15,
		},
	})
		.then((result) => successStatus(result, props))
		.catch((error) => errorStatus(error, props));

export const requestAllNodes = <T extends Node>(type: string): Promise<RequestStatus<Error, Array<T>>> =>
	request<Array<T>>(`/api/node/${type}`, {
		query: {
			fullNode: true,
			timeoutSeconds: 15,
		},
	})
		.then(successStatus)
		.catch(errorStatus);

export const persistNode = <T extends Node>(node: T, timeoutSeconds = 15): Promise<ResponseStatus<Error, void>> =>
	request<void>("/api/node/_", {
		method: "POST",
		query: {
			timeoutSeconds,
		},
		body: [node],
	}) // TODO: read timeoutSeconds from config
		.then(successStatus)
		.catch(errorStatus);

export const deleteNode = (nodeId: NodeId): Promise<ResponseStatus<Error, void>> =>
	request<void>(`/api/node/_/${nodeId}`, {
		method: "DELETE",
		query: {
			timeoutSeconds: 15,
		},
	})
		.then(successStatus)
		.catch(errorStatus);

export const deleteNodeAdvanced = <T extends Node>(element: T): Promise<void> =>
	request<void>(`/api/node/${element._type}/${element.id!}/delete`, {
		method: "POST",
		accept: "text",
		query: {
			timeoutSeconds: 15,
			force: true,
		},
		body: "{}",
	});

export const deleteNodeAdvancedStatus = <T extends Node>(element: T): Promise<ResponseStatus<Error, void>> =>
	deleteNodeAdvanced(element).then(successStatus).catch(errorStatus);

export const requestNodeObservable = toRequestObservable(requestNode);
export const requestAllNodesObservable = toRequestObservable(requestAllNodes);
export const persistNodeObservable = toRequestObservable(persistNode);
export const deleteNodeObservable = toRequestObservable(deleteNode);
export const deleteNodeAdvancedObservable = toRequestObservable(deleteNodeAdvancedStatus);
